import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, ArrowRightLeft, Mail, Phone, Building2, ShieldCheck, MapPin, Calendar, Plane } from 'lucide-react';
import { fileUrl } from '@/components/Uploaders';
import { fmtDate, fmtDateTime } from '@/lib/dates';
import { StatusBadge } from '@/components/StatusBadge';
import { toast } from 'sonner';

const TINGKAT_OPTIONS = ['Wali Kota', 'Wakil Wali Kota', 'Sekda', 'Asisten', 'Staf Ahli', 'Kepala Dinas', 'Kepala Badan', 'Kepala Bagian', 'Camat', 'Lurah'];
const MANUAL_STATUS = ['Di Kantor', 'Cuti', 'Tidak Diketahui'];

const STATUS_COLORS = {
  'Di Kantor': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Sedang Agenda': 'bg-sky-50 text-sky-700 border-sky-200',
  'Sedang Dinas DN': 'bg-amber-50 text-amber-700 border-amber-200',
  'Sedang Dinas LN': 'bg-red-50 text-red-700 border-red-200',
  'Cuti': 'bg-slate-100 text-slate-700 border-slate-200',
  'Tidak Diketahui': 'bg-slate-100 text-slate-500 border-slate-200',
};

export default function PejabatDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pej, setPej] = useState(null);
  const [opds, setOpds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mutasiOpen, setMutasiOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [mutasi, setMutasi] = useState({ jabatan_baru: '', tingkat_jabatan_baru: '', opd_id_baru: '', pangkat_baru: '', golongan_baru: '', tanggal_mutasi: new Date().toISOString().slice(0, 10), sk_nomor: '', catatan: '' });
  const [statusForm, setStatusForm] = useState({ status_keberadaan: 'Cuti', catatan: '', sampai_tanggal: '' });

  const load = async () => {
    setLoading(true);
    try {
      const [p, o] = await Promise.all([api.get(`/pejabat/${id}`), api.get('/opd')]);
      setPej(p.data);
      setOpds(o.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(load, [id]);

  const submitMutasi = async () => {
    try {
      await api.post(`/pejabat/${id}/mutasi`, { ...mutasi, pejabat_id: id });
      toast.success('Mutasi berhasil dicatat');
      setMutasiOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal');
    }
  };

  const submitStatus = async () => {
    try {
      await api.post(`/pejabat/${id}/status`, statusForm);
      toast.success('Status diperbarui');
      setStatusOpen(false);
      load();
    } catch (e) {
      toast.error('Gagal');
    }
  };

  if (loading || !pej) return <div className="py-16 flex justify-center"><div className="animate-spin w-6 h-6 border-2 border-red-600 border-t-transparent rounded-full" /></div>;

  const canEdit = user?.role === 'admin' || user?.role === 'operator';
  const k = pej.keberadaan || {};

  return (
    <div className="space-y-6 max-w-6xl">
      <PageHeader
        title={`${pej.gelar_depan || ''} ${pej.nama} ${pej.gelar_belakang || ''}`.trim()}
        subtitle={pej.jabatan}
        breadcrumb={[{ label: 'Beranda', to: '/dashboard' }, { label: 'Pejabat', to: '/manajemen/pejabat' }, { label: 'Detail' }]}
        action={<Button variant="outline" onClick={() => navigate(-1)}><ArrowLeft className="w-4 h-4 mr-2" />Kembali</Button>}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="border-slate-200 shadow-premium">
          <CardContent className="p-6 text-center">
            {pej.foto_url ? (
              <img src={fileUrl(pej.foto_url)} alt={pej.nama} className="w-36 h-36 mx-auto rounded-full object-cover border-4 border-white shadow-elevated" />
            ) : (
              <div className="w-36 h-36 mx-auto rounded-full bg-gradient-to-br from-[#DC2626] to-[#FBBF24] text-white text-5xl font-extrabold flex items-center justify-center shadow-elevated">
                {pej.nama?.[0]}
              </div>
            )}
            <div className="mt-4 text-xl font-extrabold">{pej.gelar_depan} {pej.nama} {pej.gelar_belakang}</div>
            <div className="text-sm text-slate-600">{pej.jabatan}</div>
            {pej.tingkat_jabatan && <span className="inline-block mt-2 px-2 py-0.5 rounded-full bg-red-50 text-red-700 text-xs font-semibold">{pej.tingkat_jabatan}</span>}
            <div className={`mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold ${STATUS_COLORS[k.status] || ''}`}>
              <span className="w-2 h-2 rounded-full bg-current" /> {k.status || 'Di Kantor'}
            </div>
            {k.detail && <div className="text-xs text-slate-500 mt-1"><MapPin className="w-3 h-3 inline" /> {k.detail}</div>}
            {!pej.is_active && <div className="mt-2 px-2 py-0.5 inline-block bg-slate-100 text-slate-600 text-[10px] font-bold rounded uppercase">Nonaktif</div>}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2 border-slate-200 shadow-premium">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-bold">Data Lengkap</CardTitle>
              {canEdit && (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setStatusOpen(true)}><ShieldCheck className="w-4 h-4 mr-2" />Set Status</Button>
                  <Button variant="outline" size="sm" onClick={() => setMutasiOpen(true)} data-testid="mutasi-button"><ArrowRightLeft className="w-4 h-4 mr-2" />Mutasi Jabatan</Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="divide-y divide-slate-100">
            <Row label="NIP" value={pej.nip || '-'} mono />
            <Row label="Pangkat / Golongan" value={`${pej.pangkat || '-'} (${pej.golongan || '-'})`} />
            <Row label="OPD" value={pej.opd_nama || '-'} icon={Building2} />
            <Row label="Email" value={pej.email || '-'} icon={Mail} />
            <Row label="No. HP" value={pej.phone || '-'} icon={Phone} />
            <Row label="No. WhatsApp" value={pej.whatsapp_number || pej.phone || '-'} icon={Phone} />
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200 shadow-premium">
        <CardHeader><CardTitle className="text-base font-bold">Riwayat Jabatan / Mutasi</CardTitle></CardHeader>
        <CardContent>
          {(pej.mutasi_history || []).length === 0 ? (
            <div className="text-sm text-slate-500 italic py-3">Belum ada riwayat mutasi.</div>
          ) : (
            <div className="space-y-3">
              {pej.mutasi_history.map((m, i) => (
                <div key={m.id} className="flex gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50/30">
                  <div className="w-10 h-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center flex-shrink-0">
                    <ArrowRightLeft className="w-4 h-4" />
                  </div>
                  <div className="flex-1">
                    <div className="text-xs font-mono text-slate-500">{fmtDate(m.tanggal_mutasi)} · SK: {m.sk_nomor || '-'}</div>
                    <div className="text-sm mt-1">
                      <span className="text-slate-500 line-through">{m.jabatan_lama}</span>
                      <ArrowRightLeft className="w-3 h-3 inline mx-2 text-slate-400" />
                      <span className="font-semibold text-slate-800">{m.jabatan_baru}</span>
                    </div>
                    {m.opd_nama_baru && m.opd_nama_baru !== m.opd_nama_lama && (
                      <div className="text-xs text-slate-500 mt-0.5">OPD: {m.opd_nama_lama} → <strong>{m.opd_nama_baru}</strong></div>
                    )}
                    {m.catatan && <div className="text-xs text-slate-600 mt-1 italic">“{m.catatan}”</div>}
                    <div className="text-[10px] text-slate-400 mt-1">Dicatat oleh {m.created_by_nama} · {fmtDateTime(m.created_at)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Mutasi Dialog */}
      <Dialog open={mutasiOpen} onOpenChange={setMutasiOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Mutasi Jabatan - {pej.nama}</DialogTitle></DialogHeader>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="md:col-span-2"><Label>Jabatan Baru *</Label><Input value={mutasi.jabatan_baru} onChange={(e) => setMutasi({ ...mutasi, jabatan_baru: e.target.value })} placeholder="Kepala Dinas Pariwisata" /></div>
            <div>
              <Label>Tingkat Jabatan Baru</Label>
              <Select value={mutasi.tingkat_jabatan_baru || pej.tingkat_jabatan} onValueChange={(v) => setMutasi({ ...mutasi, tingkat_jabatan_baru: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{TINGKAT_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>OPD Baru</Label>
              <Select value={mutasi.opd_id_baru || 'none'} onValueChange={(v) => setMutasi({ ...mutasi, opd_id_baru: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="Pilih OPD" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">- Tetap di OPD sebelumnya -</SelectItem>
                  {opds.map((o) => <SelectItem key={o.id} value={o.id}>{o.nama}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Pangkat Baru</Label><Input value={mutasi.pangkat_baru} onChange={(e) => setMutasi({ ...mutasi, pangkat_baru: e.target.value })} /></div>
            <div><Label>Golongan Baru</Label><Input value={mutasi.golongan_baru} onChange={(e) => setMutasi({ ...mutasi, golongan_baru: e.target.value })} /></div>
            <div><Label>Tanggal Mutasi *</Label><Input type="date" value={mutasi.tanggal_mutasi} onChange={(e) => setMutasi({ ...mutasi, tanggal_mutasi: e.target.value })} /></div>
            <div><Label>Nomor SK</Label><Input value={mutasi.sk_nomor} onChange={(e) => setMutasi({ ...mutasi, sk_nomor: e.target.value })} placeholder="821.2/XXX/SK/2026" /></div>
            <div className="md:col-span-2"><Label>Catatan</Label><Textarea rows={2} value={mutasi.catatan} onChange={(e) => setMutasi({ ...mutasi, catatan: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMutasiOpen(false)}>Batal</Button>
            <Button onClick={submitMutasi} className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="submit-mutasi">Catat Mutasi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Dialog */}
      <Dialog open={statusOpen} onOpenChange={setStatusOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Set Status Keberadaan</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Status SPPD & Agenda akan otomatis terdeteksi. Gunakan ini untuk status manual seperti <strong>Cuti</strong>.</p>
          <div className="space-y-3">
            <div>
              <Label>Status</Label>
              <Select value={statusForm.status_keberadaan} onValueChange={(v) => setStatusForm({ ...statusForm, status_keberadaan: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{MANUAL_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Berlaku Sampai</Label><Input type="date" value={statusForm.sampai_tanggal} onChange={(e) => setStatusForm({ ...statusForm, sampai_tanggal: e.target.value })} /></div>
            <div><Label>Catatan</Label><Textarea rows={2} value={statusForm.catatan} onChange={(e) => setStatusForm({ ...statusForm, catatan: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusOpen(false)}>Batal</Button>
            <Button onClick={submitStatus} className="bg-[#DC2626] hover:bg-[#B91C1C]">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value, icon: Icon, mono }) {
  return (
    <div className="py-3 flex items-start gap-3">
      {Icon && <Icon className="w-4 h-4 text-slate-400 mt-0.5" />}
      <div className="flex-1">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
        <div className={`text-sm text-slate-800 font-medium mt-0.5 ${mono ? 'font-mono' : ''}`}>{value}</div>
      </div>
    </div>
  );
}
